/**
 * Generic filterable search with suggestions.
 * External list updates only on suggestion select or input blur.
 *
 * @example
 * el.dataSource = ['arrow-left', 'circle-arrow-left'];
 * el.addEventListener('filterable-search:change', (event) => {
 *   const { query, matches, reason, selected } = event.detail;
 * });
 */
class FilterableSearch extends HTMLElement {
  static MAX_SUGGESTIONS = 8;
  static MAX_LENGTH = 64;
  static ALLOWED_PATTERN = /[^a-zA-Z0-9\-_\s]/g;

  /** @type {string[]} */
  #dataSource = [];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLUListElement | null} */
  #listbox = null;

  /** @type {string[]} */
  #suggestions = [];

  /** @type {number} */
  #activeIndex = -1;

  /** @type {string | null} */
  #lastCommittedQuery = null;

  /** @type {boolean} */
  #skipNextBlurCommit = false;

  connectedCallback() {
    this.#renderShell();
    this.#bindEvents();
    this.#readDataSourceAttribute();
  }

  disconnectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * @param {string[]} value
   */
  set dataSource(value) {
    this.#dataSource = Array.isArray(value)
      ? value.filter((item) => typeof item === 'string' && item.trim() !== '')
      : [];
  }

  /**
   * @returns {string[]}
   */
  get dataSource() {
    return this.#dataSource.slice();
  }

  #renderShell() {
    if (this.querySelector('.filterable-search__input')) {
      this.#input = this.querySelector('.filterable-search__input');
      this.#listbox = this.querySelector('.filterable-search__listbox');
      return;
    }

    const uid = this.#uniqueId();
    const inputId = `filterable-search-input-${uid}`;
    const listboxId = `filterable-search-listbox-${uid}`;

    this.classList.add('filterable-search');

    const label = document.createElement('label');
    label.className = 'sr-only';
    label.setAttribute('for', inputId);
    label.textContent = this.getAttribute('label') || 'Search';

    const input = document.createElement('input');
    input.id = inputId;
    input.className = 'filterable-search__input';
    input.type = 'search';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listboxId);
    input.setAttribute('aria-haspopup', 'listbox');
    input.placeholder = this.getAttribute('placeholder') || 'Search…';

    const listbox = document.createElement('ul');
    listbox.id = listboxId;
    listbox.className = 'filterable-search__listbox';
    listbox.setAttribute('role', 'listbox');
    listbox.hidden = true;

    this.append(label, input, listbox);
    this.#input = input;
    this.#listbox = listbox;
  }

  #bindEvents() {
    if (!this.#input || !this.#listbox) return;

    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#input.addEventListener('input', () => this.#onInput(), { signal });
    this.#input.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.#input.addEventListener('blur', () => this.#onBlur(), { signal });
    this.#listbox.addEventListener('mousedown', (event) => this.#onListboxMouseDown(event), {
      signal,
    });
  }

  #readDataSourceAttribute() {
    const raw = this.getAttribute('data-source');
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      this.dataSource = parsed;
    } catch (error) {
      console.warn('[filterable-search] Invalid data-source JSON', error);
      this.dataSource = [];
    }
  }

  #uniqueId() {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #sanitize(value) {
    return String(value || '')
      .replace(FilterableSearch.ALLOWED_PATTERN, '')
      .slice(0, FilterableSearch.MAX_LENGTH);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  /**
   * @param {string} query
   * @returns {string[]}
   */
  #getMatches(query) {
    const sanitized = this.#sanitize(query).trim();
    if (sanitized === '') {
      return this.#dataSource.slice();
    }

    const normalizedQuery = this.#normalize(sanitized);
    if (normalizedQuery === '') {
      return [];
    }

    return this.#dataSource.filter((item) => this.#normalize(item).includes(normalizedQuery));
  }

  /**
   * @param {string} query
   * @returns {string[]}
   */
  #getSuggestions(query) {
    const sanitized = this.#sanitize(query).trim();
    if (sanitized === '') {
      return [];
    }

    const normalizedQuery = this.#normalize(sanitized);
    if (normalizedQuery === '') {
      return [];
    }

    /** @type {string[]} */
    const prefixMatches = [];
    /** @type {string[]} */
    const containsMatches = [];

    for (const item of this.#dataSource) {
      const normalizedItem = this.#normalize(item);
      if (!normalizedItem.includes(normalizedQuery)) continue;

      if (normalizedItem.startsWith(normalizedQuery)) {
        prefixMatches.push(item);
      } else {
        containsMatches.push(item);
      }
    }

    return prefixMatches.concat(containsMatches).slice(0, FilterableSearch.MAX_SUGGESTIONS);
  }

  #onInput() {
    if (!this.#input) return;

    const caret = this.#input.selectionStart;
    const sanitized = this.#sanitize(this.#input.value);

    if (sanitized !== this.#input.value) {
      this.#input.value = sanitized;
      if (typeof caret === 'number') {
        const nextCaret = Math.min(caret, sanitized.length);
        this.#input.setSelectionRange(nextCaret, nextCaret);
      }
    }

    this.#renderSuggestions(sanitized);
  }

  /**
   * @param {string} query
   */
  #renderSuggestions(query) {
    if (!this.#listbox || !this.#input) return;

    this.#suggestions = this.#getSuggestions(query);
    this.#activeIndex = -1;
    this.#listbox.replaceChildren();

    if (this.#suggestions.length === 0) {
      this.#closeSuggestions();
      return;
    }

    this.#suggestions.forEach((value, index) => {
      const option = document.createElement('li');
      option.className = 'filterable-search__option';
      option.id = `${this.#listbox.id}-option-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.dataset.value = value;
      option.textContent = value;
      this.#listbox.appendChild(option);
    });

    this.#listbox.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
    this.#input.removeAttribute('aria-activedescendant');
  }

  #closeSuggestions() {
    if (!this.#listbox || !this.#input) return;

    this.#suggestions = [];
    this.#activeIndex = -1;
    this.#listbox.replaceChildren();
    this.#listbox.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.removeAttribute('aria-activedescendant');
  }

  /**
   * @param {number} index
   */
  #setActiveIndex(index) {
    if (!this.#listbox || !this.#input) return;

    const options = this.#listbox.querySelectorAll('.filterable-search__option');
    if (options.length === 0) return;

    const nextIndex = ((index % options.length) + options.length) % options.length;
    this.#activeIndex = nextIndex;

    options.forEach((option, optionIndex) => {
      const isActive = optionIndex === nextIndex;
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        this.#input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    const isOpen = this.#listbox && !this.#listbox.hidden && this.#suggestions.length > 0;

    switch (event.key) {
      case 'ArrowDown':
        if (!isOpen) {
          this.#renderSuggestions(this.#sanitize(this.#input?.value || ''));
        }
        if (this.#suggestions.length > 0) {
          event.preventDefault();
          this.#setActiveIndex(this.#activeIndex + 1);
        }
        break;
      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault();
          this.#setActiveIndex(this.#activeIndex <= 0 ? this.#suggestions.length - 1 : this.#activeIndex - 1);
        }
        break;
      case 'Enter':
        if (isOpen && this.#activeIndex >= 0) {
          event.preventDefault();
          this.#selectSuggestion(this.#suggestions[this.#activeIndex]);
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.#closeSuggestions();
        }
        break;
      default:
        break;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onListboxMouseDown(event) {
    const option = event.target instanceof Element
      ? event.target.closest('.filterable-search__option')
      : null;

    if (!option) return;

    // Keep input focused so blur does not race the select commit.
    event.preventDefault();
    const value = option.getAttribute('data-value');
    if (value) {
      this.#selectSuggestion(value);
    }
  }

  /**
   * @param {string} value
   */
  #selectSuggestion(value) {
    if (!this.#input) return;

    this.#skipNextBlurCommit = true;
    this.#input.value = value;
    this.#closeSuggestions();
    this.#commit({
      query: value,
      reason: 'select',
      selected: value,
    });

    // Focus stays on input after mousedown preventDefault; clear the flag
    // so a later blur still commits new typed queries.
    window.setTimeout(() => {
      this.#skipNextBlurCommit = false;
    }, 0);
  }

  #onBlur() {
    // Defer so a prevented mousedown select can set the skip flag first.
    window.setTimeout(() => {
      if (this.#skipNextBlurCommit) {
        this.#skipNextBlurCommit = false;
        this.#closeSuggestions();
        return;
      }

      this.#closeSuggestions();
      this.#commit({
        query: this.#sanitize(this.#input?.value || ''),
        reason: 'blur',
        selected: null,
      });
    }, 0);
  }

  /**
   * @param {{ query: string, reason: 'select' | 'blur', selected: string | null }} payload
   */
  #commit(payload) {
    const query = this.#sanitize(payload.query).trim();

    if (this.#lastCommittedQuery === query && payload.reason === 'blur') {
      return;
    }

    this.#lastCommittedQuery = query;
    const matches = this.#getMatches(query);

    this.dispatchEvent(
      new CustomEvent('filterable-search:change', {
        bubbles: true,
        detail: {
          query,
          matches,
          reason: payload.reason,
          selected: payload.selected,
        },
      })
    );
  }
}

if (!customElements.get('filterable-search')) {
  customElements.define('filterable-search', FilterableSearch);
}
